import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import { AlertService } from '../../../core/services/alert.service';

type InputValue = {
  id: number;
  title: string;
};
type ChipView =
  | { kind: 'item'; item: InputValue }
  | {
      kind: 'group';
      base: string;
      title: string;
      members: InputValue[];
      count: number;
    };

@Component({
  selector: 'app-chips-input',
  standalone: true,
  imports: [
    CommonModule,
    MatInputModule,
    MatChipsModule,
    MatIconModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
  ],
  templateUrl: './chips-input.component.html',
  styleUrls: ['./chips-input.component.sass'],
})
export class ChipsInputComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly alertService = inject(AlertService);

  private _selectedValues = new BehaviorSubject<InputValue[]>([]);
  selectedValues$ = this._selectedValues.asObservable();

  private _data = new BehaviorSubject<InputValue[]>([]);
  @Input() set data(val: InputValue[]) {
    this._data.next(val ?? []);
  }
  @Input() debounce = 1000;
  @Input() placeHolder = '';
  @Input() maxValue: number | undefined = undefined;
  @Input() inputLabel = '';
  @Input() appearance: 'outline' | 'fill' = 'outline';

  @Input() set selectedValues(value: InputValue[]) {
    this._selectedValues.next(value ?? []);
    this.recomputeView();
  }

  private _disabled = false;
  @Input() set disabled(val: boolean) {
    this._disabled = val;
    val ? this.valueCtrl.disable() : this.valueCtrl.enable();
    this.valueCtrl.updateValueAndValidity();
  }
  get disabled(): boolean {
    return this._disabled;
  }

  @Input() enableSearch = false;

  @Input() groupVisually = false;

  @Input() groupMinCount = 3;

  @Input() groupLabelMap: Record<string, string> = {};

  private expandedBases = new Set<string>();

  @Output() selectedValuesChange = new EventEmitter<InputValue[]>();
  @Output() searchInput = new EventEmitter<string>();

  valueCtrl = new FormControl<string | InputValue>('');

  private _viewChips = new BehaviorSubject<ChipView[]>([]);
  viewChips$ = this._viewChips.asObservable();

  filteredData: Observable<InputValue[]> = combineLatest([
    this._selectedValues.asObservable(),
    this._data.asObservable(),
  ]).pipe(
    map(([selected, data]) =>
      data.filter((item) => !selected.find((sel) => sel.id === item.id))
    ),
    switchMap((data) =>
      this.valueCtrl.valueChanges.pipe(
        startWith(''),
        map((str = '') => {
          const q =
            typeof str === 'string'
              ? str.toLowerCase()
              : str.title.toLowerCase();
          return data.filter((item) => item.title.toLowerCase().includes(q));
        })
      )
    )
  );

  @ViewChild('valueInput') valueInput: ElementRef<HTMLInputElement>;

  private readonly destroy$ = new Subject<void>();

  constructor() {}

  ngOnInit(): void {
    combineLatest([this.selectedValues$, this._data.asObservable()])
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.recomputeView());

    this.valueCtrl.valueChanges
      .pipe(
        debounceTime(this.debounce),
        filter((value) => typeof value === 'string' && value.trim().length > 0),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((item: string) => {
        if (this.enableSearch && !this.disabled) {
          this.searchInput.emit(item.trim());
        }
      });
  }

  ngAfterViewInit(): void {
    this._disabled ? this.valueCtrl.disable() : this.valueCtrl.enable();
    this.valueCtrl.updateValueAndValidity();
  }

  /** ====== Agrupación visual ====== */

  private parseBase(title: string): {
    base: string | null;
    num: number | null;
  } {
    const m = title.trim().match(/^(.+?)[\s-]*([0-9]+)$/i);
    if (!m) return { base: null, num: null };
    return { base: m[1].trim(), num: Number(m[2]) };
  }

  private groupLabel(base: string): string {
    return this.groupLabelMap[base] ?? base;
  }

  private recomputeView(): void {
    const selected = this._selectedValues.value ?? [];
    if (!this.groupVisually) {
      this._viewChips.next(selected.map((item) => ({ kind: 'item', item })));
      return;
    }

    const groups = new Map<string, InputValue[]>();
    const singles: InputValue[] = [];

    for (const it of selected) {
      const { base } = this.parseBase(it.title);
      if (base) {
        const arr = groups.get(base) ?? [];
        arr.push(it);
        groups.set(base, arr);
      } else {
        singles.push(it);
      }
    }

    const view: ChipView[] = [];

    for (const it of singles) view.push({ kind: 'item', item: it });

    for (const [base, members] of groups) {
      const shouldGroup =
        members.length >= this.groupMinCount && !this.expandedBases.has(base);
      if (shouldGroup) {
        view.push({
          kind: 'group',
          base,
          title: this.groupLabel(base),
          members: members
            .slice()
            .sort((a, b) =>
              a.title.localeCompare(b.title, undefined, { numeric: true })
            ),
          count: members.length,
        });
      } else {
        for (const it of members) view.push({ kind: 'item', item: it });
      }
    }

    view.sort((a, b) => {
      const ta = a.kind === 'group' ? a.title : a.item.title;
      const tb = b.kind === 'group' ? b.title : b.item.title;
      return ta.localeCompare(tb, undefined, { numeric: true });
    });

    this._viewChips.next(view);
  }

  /** ====== Eventos de UI ====== */

  removeChip(chip: ChipView): void {
    if (chip.kind === 'group') {
      this.expandedBases.add(chip.base);
      this.recomputeView();
      return;
    }

    const list = this._selectedValues.value.filter(
      (item) => item.id !== chip.item.id
    );
    this.selectedValuesChange.emit(list);
    this._selectedValues.next(list);
    this.recomputeView();
  }

  remove(value: InputValue): void {
    this.removeChip({ kind: 'item', item: value });
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    if (this.maxValue && this._selectedValues.value.length >= this.maxValue) {
      this.valueCtrl.setValue('');
      this.valueInput.nativeElement.value = '';
      this.alertService.error(
        'No puedes agregar mas de ' + this.maxValue + ' elementos'
      );
      return;
    }
    const { value } = event.option as { value: InputValue };
    this.valueCtrl.setValue('');
    this.valueInput.nativeElement.value = '';

    const current = this._selectedValues.value ?? [];
    if (!current.some((v) => v.id === value.id)) {
      const next = [...current, value];
      this.selectedValuesChange.emit(next);
      this._selectedValues.next(next);

      const { base } = this.parseBase(value.title);
      if (base && this.expandedBases.has(base)) {
        this.expandedBases.delete(base);
      }

      this.recomputeView();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
